import { MockedAuthManager } from "../../contexts/AuthManager";
import { MockedProvider } from "@apollo/client/testing/react";
import React, { useMemo } from "react";
import { createNativeStackNavigator } from "@react-navigation/native-stack";

import {
    buildClient,
    buildClientProfile,
    buildClientUser,
    buildIjmGroup,
    buildMission,
    buildUser,
    getAvatarUrl,
    getAvatarUrl2,
} from "../factories";
import type { Meta, StoryObj } from "@storybook/react";
import { Button, View } from "react-native";
import Callout from "../../ui_atoms/Callout";
import { MockedProfileSwitcherManager } from "../../contexts/ProfileSwitcherManager";
import { StackScreenProps } from "@react-navigation/stack";
import MissionSelectSignerScreen from "../../screens/user/MissionSelectSignerScreen";
import { GET_CLIENT_USERS } from "../../graphql/client";
import { ProfileType } from "../../__generated__/graphql";
import { CHANGE_MISSION_SIGNER } from "../../graphql/core";
import { StoryStackNavigatorContainer } from "../display-helpers";
import { MockLink } from "@apollo/client/testing";

type StoryStackParams = {
    Home: undefined;
    MissionSelectSignerScreen: { missionId: string; showCurrentProfile?: boolean };
};

const HomeFakeScreen: React.FC<StackScreenProps<StoryStackParams, "Home">> = ({ navigation }) => {
    return (
        <View>
            <Callout markdown="Home screen" />
            <Button
                title="Back to preview"
                onPress={() => {
                    navigation.reset({
                        index: 0,
                        routes: [
                            {
                                name: "MissionSelectSignerScreen",
                                params: { missionId: "test-mission" },
                            },
                        ],
                    });
                }}
            />
        </View>
    );
};

const Stack = createNativeStackNavigator<StoryStackParams>();

const StoryComponent = ({ hasOther }: { hasOther: boolean }) => {
    const user = useMemo(
        () =>
            buildUser({
                clientProfiles: [
                    buildClientProfile({
                        id: "client",
                        client: buildClient({
                            canChangeIjmGroup: true,
                            ijmGroup: buildIjmGroup({
                                id: "GM-G1-730-2",
                                name: "2 jours de délai d'attente",
                            }),
                            ijmPayFirstDays: true,
                            ijmPayRemainingPercents: 20,
                        }),
                    }),
                ],
            }),
        [],
    );
    const mock = useMemo<MockLink.MockedResponse[]>(
        () => [
            {
                request: {
                    query: GET_CLIENT_USERS,
                    variables: () => true,
                },
                result: () => ({
                    data: {
                        getClientUsers: hasOther
                            ? [
                                  buildClientUser({ id: "the-client-profile-owner" }),
                                  buildClientUser({
                                      avatarUrl: getAvatarUrl(),
                                      firstName: "Mokoto",
                                      lastName: "Kusanagi",
                                  }),
                                  buildClientUser({
                                      avatarUrl: getAvatarUrl2(),
                                      firstName: "David",
                                      lastName: "Saradini",
                                      canSignContracts: false,
                                  }),
                                  buildClientUser({
                                      id: "client",
                                      avatarUrl: getAvatarUrl(),
                                      firstName: "Self",
                                      lastName: "Should not display",
                                  }),
                              ]
                            : [
                                  buildClientUser({
                                      id: "client",
                                      avatarUrl: getAvatarUrl(),
                                      firstName: "Self",
                                      lastName: "Should not display",
                                  }),
                              ],
                    },
                }),
            },
            {
                request: {
                    query: CHANGE_MISSION_SIGNER,
                    variables: () => true,
                },
                result: () => ({
                    data: {
                        changeMissionSigner: buildMission(),
                    },
                }),
            },
        ],
        [hasOther],
    );

    return (
        <MockedAuthManager user={user}>
            <MockedProfileSwitcherManager profileType={ProfileType.Client} profileId={"client"}>
                <MockedProvider mocks={mock}>
                    <StoryStackNavigatorContainer>
                        <Stack.Navigator initialRouteName="MissionSelectSignerScreen">
                            <Stack.Screen
                                name="MissionSelectSignerScreen"
                                component={MissionSelectSignerScreen}
                                initialParams={{ missionId: `test-mission` }}
                                options={{
                                    gestureEnabled: false,
                                    headerShown: true,
                                    title: "",
                                    headerBackTitle: "Back",
                                    headerBackButtonDisplayMode: "default",
                                }}
                            />
                            <Stack.Screen name="Home" component={HomeFakeScreen} />
                        </Stack.Navigator>
                    </StoryStackNavigatorContainer>
                </MockedProvider>
            </MockedProfileSwitcherManager>
        </MockedAuthManager>
    );
};

const meta = {
    title: "Screens/Change Signer",
    component: StoryComponent,
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Default: Story = {
    args: {
        hasOther: true,
    },
};

export const NoOther: Story = {
    args: {
        hasOther: false,
    },
};
